<template>
  <AppDialog
    v-if="activeStatus"
    :open="true"
    :dismissible="false"
    :title="dialogContent.title"
    :description="dialogContent.message"
  >
    <template #illustration>
      <LoaderCircle
        v-if="activeStatus === APP_STATUS.CHECKING || activeStatus === APP_STATUS.SESSION"
        class="tw:mx-auto tw:animate-spin tw:text-link tw:icon-dialog"
      />
      <CircleAlert v-else class="tw:mx-auto tw:text-link tw:icon-dialog" />
    </template>
    <template v-if="dialogContent.action" #footer>
      <Button :disabled="isRetrying" @click="handleAction">{{ dialogContent.action }}</Button>
    </template>
  </AppDialog>
</template>
<script setup>
// 1. Import necessary components
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import AppDialog from './AppDialog.vue';
import { Button } from './ui/button';
import { LoaderCircle, CircleAlert } from '@lucide/vue';
import { useAppVersionGate } from '@/shared/composables/useAppVersionGate';
import { useNetworkStatus } from '@/shared/composables/useNetworkStatus';
import { useAuthStore } from '@/core/stores/auth.store';

// 2. Use
const { t } = useI18n();
const auth = useAuthStore();
const emit = defineEmits(['ready', 'startup-error']);

const APP_STATUS = Object.freeze({
  CHECKING: 'checking',
  SESSION: 'session',
  OFFLINE: 'offline',
  UPDATE_REQUIRED: 'update-required',
  CHECK_FAILED: 'check-failed',
});

// 3. Define a reactive variable
const {
  isUpdateRequired,
  hasVersionCheckFailed,
  isCheckingVersion,
  hasCompletedInitialCheck,
  updateUrl,
  checkAppVersionNow,
} = useAppVersionGate();
const {
  isConnected,
  isNetworkReady,
  refreshNetworkStatus,
  startNetworkMonitoring,
  stopNetworkMonitoring,
} = useNetworkStatus();

const isRetrying = ref(false);

// 4. Define methods or computed properties if needed
// 4.1 Define methods
const handleAction = async () => {
  if (isRetrying.value) {
    return;
  }
  if (activeStatus.value === APP_STATUS.UPDATE_REQUIRED) {
    window.open(updateUrl.value, '_blank', 'noopener');
    return;
  }

  isRetrying.value = true;

  try {
    // A version failure retries the version endpoint, not the connectivity check.
    if (activeStatus.value === APP_STATUS.CHECK_FAILED) {
      await checkAppVersionNow();
    } else {
      await refreshNetworkStatus();
    }
  } catch {
    // Keep the blocking state and its retry action when the network plugin fails.
  } finally {
    isRetrying.value = false;
  }
};

// 4.2 Define computed properties
const activeStatus = computed(() => {
  if (isNetworkReady.value && !isConnected.value) {
    return APP_STATUS.OFFLINE;
  }
  if (isUpdateRequired.value) {
    return APP_STATUS.UPDATE_REQUIRED;
  }
  if (hasVersionCheckFailed.value) {
    return APP_STATUS.CHECK_FAILED;
  }
  if (isCheckingVersion.value && !hasCompletedInitialCheck.value) {
    return APP_STATUS.CHECKING;
  }
  if (!auth.isAuthReady && auth.isLoggedIn) {
    return APP_STATUS.SESSION;
  }
  return '';
});

const dialogContent = computed(() => {
  if (activeStatus.value === APP_STATUS.SESSION) {
    return {
      title: t('appStatus.checkingSessionTitle'),
      message: t('appStatus.checkingMessage'),
      action: '',
    };
  }
  if (activeStatus.value === APP_STATUS.OFFLINE) {
    return {
      title: t('appStatus.offlineTitle'),
      message: t('appStatus.offlineMessage'),
      action: t('appStatus.retry'),
    };
  }

  if (activeStatus.value === APP_STATUS.UPDATE_REQUIRED) {
    return {
      title: t('appStatus.updateTitle'),
      message: t('appStatus.updateMessage'),
      action: t('appStatus.update'),
    };
  }

  if (activeStatus.value === APP_STATUS.CHECK_FAILED) {
    return {
      title: t('appStatus.checkFailedTitle'),
      message: t('appStatus.checkFailedMessage'),
      action: t('appStatus.retry'),
    };
  }

  return {
    title: t('appStatus.checkingTitle'),
    message: t('appStatus.checkingMessage'),
    action: '',
  };
});

// 5. VueJS Hooks
watch(isConnected, (connected, wasConnected) => {
  if (connected && wasConnected === false) {
    void checkAppVersionNow();
  }
});

onMounted(async () => {
  try {
    await startNetworkMonitoring();

    // Release requirements apply to the installed app, not to a patient session.
    if (isConnected.value) {
      await checkAppVersionNow();
    }
  } catch {
    emit('startup-error');
    return;
  }

  // Reveal actionable offline/update/retry feedback as well as a supported app.
  emit('ready');
});

onBeforeUnmount(async () => {
  await stopNetworkMonitoring();
});
</script>
